import { useCallback } from 'react';
import { useFormik } from 'formik';
import { useQueryClient } from '@tanstack/react-query';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import { getDocumentName } from '@e-socna/data-layer';
import { EMPTY_STRING } from '@e-socna/empty';

import { AuthenticationContextProvider_AuthenticatedUser_QueryDocument } from '../../contexts/AuthenticationContext';
import { PasswordField } from '../../components/PasswordField';
import { describeErrors } from '../../utils/back-errors.util';
import { AlertMessages } from '../../components/AlertMessages';

import { usePasswordChangeMutation } from './hooks/usePasswordChangeMutation.hook';
import { passwordChangeForm2ApiMapper } from './mappers/password-change.form2api.mapper';
import { PasswordChangeSchema } from './schemas/password-change.schema';
import type { PasswordChangeFormValues } from './types/password-change-form-values.type';

const INITIAL_VALUES: PasswordChangeFormValues = {
  newPassword: EMPTY_STRING,
  newPasswordConfirmation: EMPTY_STRING,
  currentPassword: EMPTY_STRING,
};

const PASSWORD_FIELDS = [
  {
    name: 'currentPassword',
    label: 'Mot de passe actuel',
    autoComplete: 'current-password',
  },
  {
    name: 'newPassword',
    label: 'Nouveau mot de passe',
    autoComplete: 'new-password',
  },
  {
    name: 'newPasswordConfirmation',
    label: 'Confirmation du nouveau mot de passe',
    autoComplete: 'new-password',
  },
] as const;

export const PasswordChangeForm = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, error, isPending } = usePasswordChangeMutation();

  // The refreshed authenticated user no longer has to change their password:
  // the application opens.
  const handleSubmit = useCallback(
    async (values: PasswordChangeFormValues) => {
      await mutateAsync(passwordChangeForm2ApiMapper(values))
        .then(() =>
          queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(
                AuthenticationContextProvider_AuthenticatedUser_QueryDocument,
              ),
            ],
          }),
        )
        .catch(() => undefined);
    },
    [mutateAsync, queryClient],
  );

  const formik = useFormik<PasswordChangeFormValues>({
    initialValues: INITIAL_VALUES,
    onSubmit: handleSubmit,
    validationSchema: PasswordChangeSchema,
  });

  const errorOf = (name: keyof PasswordChangeFormValues) =>
    formik.touched[name] ? formik.errors[name] : undefined;

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <Stack spacing={2}>
        {PASSWORD_FIELDS.map(({ name, label, autoComplete }) => (
          <PasswordField
            autoComplete={autoComplete}
            error={!!errorOf(name)}
            fullWidth
            helperText={errorOf(name)}
            id={`password-change-${name}`}
            key={name}
            label={label}
            name={name}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            required
            value={formik.values[name]}
          />
        ))}
        {error ? (
          <AlertMessages messages={describeErrors(error)} severity="error" />
        ) : null}
        <Button
          disabled={isPending}
          fullWidth
          id="password-change-submit"
          size="large"
          type="submit"
          variant="contained"
        >
          Changer mon mot de passe
        </Button>
      </Stack>
    </form>
  );
};
